/**
 * useStageWatcher — hostname-change watcher for cross-Stage cache flush
 * (Requirement 13.6, design.md §"9.2 SPA — cross-Stage cache clear").
 *
 * On mount the hook captures the current hostname-driven Stage via
 * {@link resolveStageFromHostname}. It then watches for in-session
 * hostname changes via three independent signals:
 *
 *   1. `popstate` (browser back/forward + manual `history.pushState`).
 *   2. `hashchange` (anchor navigation that some routers also emit).
 *   3. A 1-second polling interval, which is the only signal that
 *      catches a true hostname change (e.g. presenter pastes a new URL
 *      into the address bar in the same SPA tab) on browsers that do
 *      not fire a navigation event for a same-origin reload.
 *
 * When the resolved Stage of the current hostname differs from the
 * captured Stage:
 *
 *   - Every `localStorage` key starting with `agentmesh.` is removed.
 *   - A {@link StageFlushMessage} is posted on
 *     `BroadcastChannel("agentmesh-cache")` so any in-memory cache
 *     subscriber can flush. `BroadcastChannel` is only used when the
 *     global is defined; older browsers and jsdom (test) silently skip
 *     the post so this hook is safe to mount in any environment.
 *   - The captured Stage is updated to the new value so a subsequent
 *     change back triggers another flush.
 *
 * The hook tears down its interval and any opened `BroadcastChannel`
 * on unmount and removes its window listeners so it can be safely
 * mounted at the SPA root (task 19.4).
 */

import { useEffect } from "react";

import { resolveStageFromHostname } from "../config";

/** BroadcastChannel name shared with in-memory cache subscribers. */
export const CACHE_CHANNEL_NAME = "agentmesh-cache";

/** localStorage key prefix that the watcher considers Stage-scoped. */
export const AGENTMESH_LOCAL_STORAGE_PREFIX = "agentmesh.";

/** Polling interval for hostname-change detection, in milliseconds. */
export const STAGE_WATCHER_INTERVAL_MS = 1_000;

/**
 * Message posted on the `agentmesh-cache` BroadcastChannel when the
 * watcher detects a Stage change. Subscribers receive `{from, to}` so
 * they can log the transition before flushing.
 */
export interface StageFlushMessage {
  type: "stage-flush";
  from: "dev" | "prod";
  to: "dev" | "prod";
}

/**
 * Clears every `localStorage` key whose name starts with
 * `agentmesh.`. Errors (private mode, quota, etc.) are swallowed —
 * a flush attempt is best-effort.
 */
function clearAgentmeshLocalStorage(): void {
  if (typeof window === "undefined") return;
  let storage: Storage;
  try {
    storage = window.localStorage;
  } catch {
    return;
  }
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null && key.startsWith(AGENTMESH_LOCAL_STORAGE_PREFIX)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      try {
        storage.removeItem(key);
      } catch {
        /* per-key failure should not block the flush of the rest */
      }
    }
  } catch {
    /* iteration failure: nothing useful to do */
  }
}

/**
 * Posts a {@link StageFlushMessage} on the `agentmesh-cache`
 * BroadcastChannel when the global is available. No-op in jsdom and
 * older browsers that lack the API.
 */
function postStageFlush(from: "dev" | "prod", to: "dev" | "prod"): void {
  if (typeof BroadcastChannel === "undefined") return;
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(CACHE_CHANNEL_NAME);
    const message: StageFlushMessage = { type: "stage-flush", from, to };
    channel.postMessage(message);
  } catch {
    /* BroadcastChannel unsupported or post failed; safe to ignore */
  } finally {
    try {
      channel?.close();
    } catch {
      /* close errors are not actionable */
    }
  }
}

/**
 * React hook that watches for in-session hostname changes and flushes
 * Stage-scoped caches when the hostname-driven Stage flips. Returns
 * nothing; consumers mount it once near the SPA root.
 */
export function useStageWatcher(): void {
  useEffect(() => {
    // SSR / non-browser guard: nothing to watch without a `window`.
    if (typeof window === "undefined") return;

    let capturedStage = resolveStageFromHostname();

    const checkForChange = (): void => {
      const currentStage = resolveStageFromHostname();
      if (currentStage === capturedStage) return;
      const previousStage = capturedStage;
      // Update first so re-entrant change events do not double-flush.
      capturedStage = currentStage;
      clearAgentmeshLocalStorage();
      postStageFlush(previousStage, currentStage);
    };

    const intervalHandle = window.setInterval(
      checkForChange,
      STAGE_WATCHER_INTERVAL_MS,
    );
    window.addEventListener("popstate", checkForChange);
    window.addEventListener("hashchange", checkForChange);

    return () => {
      window.clearInterval(intervalHandle);
      window.removeEventListener("popstate", checkForChange);
      window.removeEventListener("hashchange", checkForChange);
    };
  }, []);
}
